import { format, formatDistanceToNowStrict } from "date-fns";
import {
  Archive,
  ArchiveRestore,
  Check,
  ChevronDown,
  ExternalLink,
  Pencil,
  WandSparkles,
  XCircle,
} from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useUpdateInboxItem } from "@/hooks/useUpdateInboxItem";
import type { InboxItem, SourceConfig } from "@/lib/api";
import { dotClass, sourceColor, stripeClass } from "@/lib/source-color";
import { cn } from "@/lib/utils";

interface Props {
  item: InboxItem;
  source?: SourceConfig;
  expanded: boolean;
  selected?: boolean;
  isFresh?: boolean;
  onToggle: (item: InboxItem) => void;
  onSelectedChange?: (item: InboxItem, selected: boolean) => void;
  onAnalyzeItem?: (item: InboxItem) => void;
  timeBasis?: "captured" | "source";
}

const ITEM_TYPE_OPTIONS = [
  { value: "trade_alert", label: "Trade alert" },
  { value: "watchlist", label: "Watchlist" },
  { value: "announcement", label: "Announcement" },
  { value: "info", label: "Info" },
  { value: "correction", label: "Correction" },
  { value: "vendor_model_portfolio", label: "Model portfolio" },
  { value: "buy", label: "Buy" },
  { value: "sell", label: "Sell" },
  { value: "live_class", label: "Live class" },
  { value: "marketing", label: "Marketing" },
  { value: "unknown", label: "Unknown" },
];

export function InboxItemCard({
  item,
  source,
  expanded,
  selected = false,
  isFresh = false,
  onToggle,
  onSelectedChange,
  onAnalyzeItem,
  timeBasis = "captured",
}: Props) {
  const color = sourceColor(item.source_id);
  const ts = eventTimestamp(item, timeBasis);
  const ageShort = ts
    ? formatDistanceToNowStrict(new Date(ts), { addSuffix: false })
    : "—";
  const body = inboxPreview(item);
  const symbols = item.detected_symbols ?? [];
  const candidateSymbols = item.candidate_symbols ?? [];
  const isHidden = item.status === "archived" || item.status === "ignore";
  const isUnread = item.status === "new";
  const [inlineDslEditing, setInlineDslEditing] = useState(false);
  const update = useUpdateInboxItem();

  useEffect(() => {
    setInlineDslEditing(false);
  }, [item.id]);

  return (
    <article
      id={`inbox-item-${item.id}`}
      className={cn(
        "border-b border-border border-l-2 bg-transparent transition-colors",
        stripeClass(color),
        isUnread && "bg-emerald-500/[0.035]",
        isFresh && "bg-amber-400/10 shadow-[inset_0_0_0_1px_rgb(251_191_36_/_0.35)]",
        expanded ? "bg-muted/30" : "hover:bg-muted/15",
        isHidden && "opacity-60",
      )}
    >
      <div className="flex flex-col gap-1.5 px-4 py-3 sm:px-5 sm:py-3.5">
        <header className="flex items-center gap-2 text-[11px] text-muted-fg">
          {item.id ? (
            <input
              type="checkbox"
              checked={selected}
              aria-label="Select for LLM conversion"
              onChange={(event) =>
                onSelectedChange?.(item, event.currentTarget.checked)
              }
              className="size-3.5 rounded border-border bg-bg accent-accent"
            />
          ) : null}
          <span
            className={cn(
              "size-2 shrink-0 rounded-full ring-2 ring-black/40",
              dotClass(color),
            )}
          />
          <span className="truncate font-medium">
            {source?.display_name ?? item.source_id}
          </span>
          <span aria-hidden className="hidden opacity-60 sm:inline">
            ·
          </span>
          <time
            dateTime={ts ?? undefined}
            title={ts ? format(new Date(ts), "PPpp") : undefined}
            className="shrink-0 tabular-nums"
          >
            {shortAge(ageShort)}
          </time>
          {item.status && item.status !== "new" ? (
            <Badge variant="outline" className="text-[10px] uppercase">
              {item.status}
            </Badge>
          ) : null}
          {isFresh ? (
            <Badge className="bg-amber-400 text-[10px] text-black">
              New
            </Badge>
          ) : isUnread ? (
            <Badge variant="outline" className="text-[10px] uppercase text-emerald-300">
              unread
            </Badge>
          ) : null}
          <span className="ml-auto flex shrink-0 items-center gap-2">
            <ItemTypeChip itemType={item.item_type} />
            {item.page_url ? (
              <a
                href={item.page_url}
                target="_blank"
                rel="noreferrer"
                aria-label="Open original item"
                className="rounded-sm p-0.5 text-muted-fg/70 transition-colors hover:bg-muted hover:text-fg focus:outline-none focus:ring-1 focus:ring-accent"
              >
                <ExternalLink className="size-3.5" aria-hidden />
              </a>
            ) : null}
            {onAnalyzeItem ? (
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  onAnalyzeItem(item);
                }}
                aria-label="Analyze this inbox item"
                title="Analyze this inbox item"
                className="rounded-sm p-0.5 text-muted-fg/70 transition-colors hover:bg-muted hover:text-fg focus:outline-none focus:ring-1 focus:ring-accent"
              >
                <WandSparkles className="size-3.5" aria-hidden />
              </button>
            ) : null}
            <QuickStatusActions
              item={item}
              disabled={update.isPending}
              onStatus={(status) => update.mutate({ id: item.id, status })}
            />
            <button
              type="button"
              onClick={() => onToggle(item)}
              aria-label={expanded ? "Collapse" : "Expand"}
              aria-expanded={expanded}
              className="rounded-sm p-0.5 text-muted-fg transition-colors hover:bg-muted hover:text-fg focus:outline-none focus:ring-1 focus:ring-accent"
            >
              <ChevronDown
                className={cn(
                  "size-4 transition-transform",
                  expanded && "rotate-180",
                )}
                aria-hidden
              />
            </button>
          </span>
        </header>

        {inlineDslEditing ? (
          <DslEditor
            item={item}
            compact
            onDone={() => setInlineDslEditing(false)}
          />
        ) : (
          <div
            className={cn(
              "text-sm leading-relaxed text-fg",
              expanded && !item.structured_payload
                ? "whitespace-pre-wrap break-words"
                : "line-clamp-2",
            )}
          >
            {body ? (
              item.structured_payload ? (
                <StructuredPreview
                  item={item}
                  onEdit={() => setInlineDslEditing(true)}
                />
              ) : (
                body
              )
            ) : (
              <span className="italic text-muted-fg">(no text)</span>
            )}
          </div>
        )}

        {symbols.length > 0 ? (
          <div className="flex flex-wrap items-center gap-1.5 pt-0.5">
            {symbols.map((sym) => (
              <Badge
                key={sym}
                variant="secondary"
                className="font-mono text-[11px]"
              >
                {sym}
              </Badge>
            ))}
          </div>
        ) : null}
        {candidateSymbols.length > 0 ? (
          <div className="flex flex-wrap items-center gap-1.5 pt-0.5">
            <span className="text-[10px] uppercase tracking-wider text-muted-fg/70">
              Candidate
            </span>
            {candidateSymbols.slice(0, 4).map((sym) => (
              <Link
                key={sym}
                to={`/candidates?symbol=${encodeURIComponent(sym)}`}
                className="rounded-full border border-accent/40 bg-accent/10 px-2 py-0.5 font-mono text-[11px] text-accent transition-colors hover:bg-accent/20"
              >
                {sym}
              </Link>
            ))}
            {candidateSymbols.length > 4 ? (
              <Badge variant="secondary" className="text-[10px]">
                +{candidateSymbols.length - 4}
              </Badge>
            ) : null}
          </div>
        ) : null}
      </div>

      {expanded ? <ExpandedMeta item={item} /> : null}
    </article>
  );
}

function QuickStatusActions({
  item,
  disabled,
  onStatus,
}: {
  item: InboxItem;
  disabled: boolean;
  onStatus: (status: "new" | "ack" | "archived" | "ignore") => void;
}) {
  if (item.status === "archived" || item.status === "ignore") {
    return (
      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          onStatus("new");
        }}
        disabled={disabled}
        aria-label="Restore inbox item"
        title="Restore"
        className="rounded-sm p-0.5 text-muted-fg/70 transition-colors hover:bg-muted hover:text-fg focus:outline-none focus:ring-1 focus:ring-accent disabled:opacity-50"
      >
        <ArchiveRestore className="size-3.5" aria-hidden />
      </button>
    );
  }

  return (
    <>
      {item.status !== "ack" ? (
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            onStatus("ack");
          }}
          disabled={disabled}
          aria-label="Mark inbox item read"
          title="Mark read"
          className="rounded-sm p-0.5 text-muted-fg/70 transition-colors hover:bg-muted hover:text-fg focus:outline-none focus:ring-1 focus:ring-accent disabled:opacity-50"
        >
          <Check className="size-3.5" aria-hidden />
        </button>
      ) : null}
      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          onStatus("archived");
        }}
        disabled={disabled}
        aria-label="Archive inbox item"
        title="Archive"
        className="rounded-sm p-0.5 text-muted-fg/70 transition-colors hover:bg-muted hover:text-fg focus:outline-none focus:ring-1 focus:ring-accent disabled:opacity-50"
      >
        <Archive className="size-3.5" aria-hidden />
      </button>
      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          onStatus("ignore");
        }}
        disabled={disabled}
        aria-label="Ignore inbox item"
        title="Ignore"
        className="rounded-sm p-0.5 text-muted-fg/70 transition-colors hover:bg-muted hover:text-fg focus:outline-none focus:ring-1 focus:ring-accent disabled:opacity-50"
      >
        <XCircle className="size-3.5" aria-hidden />
      </button>
    </>
  );
}

function ExpandedMeta({ item }: { item: InboxItem }) {
  const capturedAt = format(new Date(item.captured_at), "PPpp");
  const sourceTime = item.item_timestamp;
  const originalText = item.raw_text?.trim() || item.markdown?.trim() || "";
  const update = useUpdateInboxItem();
  const isArchived = item.status === "archived";
  const isIgnored = item.status === "ignore";
  const isHidden = isArchived || isIgnored;

  return (
    <div className="mx-4 mb-4 space-y-3 rounded-md border border-border/70 bg-black/25 p-3 shadow-inner sm:mx-5">
      {item.structured_payload || item.dsl_text ? (
        <DslEditor item={item} />
      ) : null}

      {originalText ? (
        <section>
          <h3 className="mb-2 text-[10px] font-medium uppercase tracking-wider text-muted-fg/80">
            Original text
          </h3>
          <div className="max-h-56 overflow-auto whitespace-pre-wrap rounded border border-border/50 bg-bg/50 p-3 text-xs leading-relaxed text-muted-fg">
            {originalText}
          </div>
        </section>
      ) : null}

      <dl className="grid grid-cols-[auto_1fr] items-center gap-x-4 gap-y-2 text-xs">
        <Row label="Type">
          <Select
            value={item.item_type ?? "unknown"}
            onValueChange={(value) =>
              update.mutate({ id: item.id, item_type: value })
            }
            disabled={update.isPending}
          >
            <SelectTrigger className="h-7 w-[160px] text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ITEM_TYPE_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Row>
        <Row label="Status">
          <Badge variant="outline" className="text-[11px]">
            {item.status}
          </Badge>
        </Row>
        <Row label="Source id">
          <code className="font-mono text-muted-fg">{item.source_id}</code>
        </Row>
        {sourceTime ? (
          <Row label="Source time">
            <span className="text-muted-fg">
              {format(new Date(sourceTime), "PPpp")}
            </span>
          </Row>
        ) : null}
        <Row label="Captured">
          <span className="text-muted-fg">{capturedAt}</span>
        </Row>
        {item.page_url ? (
          <Row label="Original item">
            <a
              href={item.page_url}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 truncate text-accent hover:underline"
            >
              <span className="truncate">{item.page_url}</span>
              <ExternalLink className="size-3 shrink-0" aria-hidden />
            </a>
          </Row>
        ) : null}
      </dl>

      {item.structured_payload ? (
        <details className="rounded border border-border/50 bg-bg/30 p-3 text-xs">
          <summary className="cursor-pointer text-[10px] font-medium uppercase tracking-wider text-muted-fg/80">
            Technical details
          </summary>
          <div className="mt-3 space-y-3">
            <SignalSummaryList payload={item.structured_payload} />
            <StructuredBlock payload={item.structured_payload} />
          </div>
        </details>
      ) : null}

      <div className="flex flex-wrap gap-2 border-t border-border/50 pt-3">
        {isHidden ? (
          <Button
            size="sm"
            variant="outline"
            onClick={() => update.mutate({ id: item.id, status: "new" })}
            disabled={update.isPending}
          >
            <ArchiveRestore />
            Restore
          </Button>
        ) : (
          <>
            {item.status !== "ack" ? (
              <Button
                size="sm"
                variant="outline"
                onClick={() => update.mutate({ id: item.id, status: "ack" })}
                disabled={update.isPending}
              >
                <Check />
                Mark read
              </Button>
            ) : null}
            <Button
              size="sm"
              variant="outline"
              onClick={() =>
                update.mutate({ id: item.id, status: "archived" })
              }
              disabled={update.isPending}
            >
              <Archive />
              Archive
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => update.mutate({ id: item.id, status: "ignore" })}
              disabled={update.isPending}
            >
              <XCircle />
              Ignore
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

function Row({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <>
      <dt className="text-[10px] font-medium uppercase tracking-wider text-muted-fg/80">
        {label}
      </dt>
      <dd className="min-w-0">{children}</dd>
    </>
  );
}

function ItemTypeChip({ itemType }: { itemType: string | null }) {
  if (!itemType) {
    return (
      <span className="text-[10px] uppercase tracking-wider text-muted-fg/60">
        —
      </span>
    );
  }
  return (
    <span className={cn("text-[10px]", chipClass(itemType))}>
      {itemType}
    </span>
  );
}

/** Stronger, accented chip styles — trade_alert/buy pop in green, sell in
 *  red, marketing/live_class sit back in amber/sky, unknown stays outlined. */
function chipClass(t: string): string {
  const base =
    "inline-flex items-center rounded-full px-2 py-0.5 font-medium whitespace-nowrap ring-1";
  switch (t) {
    case "trade_alert":
    case "buy":
      return `${base} bg-emerald-500/15 text-emerald-300 ring-emerald-500/40`;
    case "sell":
      return `${base} bg-red-500/15 text-red-300 ring-red-500/40`;
    case "watchlist":
      return `${base} bg-violet-500/15 text-violet-300 ring-violet-500/40`;
    case "announcement":
    case "info":
    case "live_class":
      return `${base} bg-sky-500/15 text-sky-300 ring-sky-500/40`;
    case "correction":
      return `${base} bg-orange-500/15 text-orange-300 ring-orange-500/40`;
    case "vendor_model_portfolio":
      return `${base} bg-teal-500/15 text-teal-300 ring-teal-500/40`;
    case "marketing":
      return `${base} bg-amber-500/15 text-amber-300 ring-amber-500/40`;
    default:
      return `${base} bg-transparent text-muted-fg ring-border`;
  }
}

function shortAge(age: string): string {
  return (
    age
      .replace(/\s*seconds?$/, "s")
      .replace(/\s*minutes?$/, "m")
      .replace(/\s*hours?$/, "h")
      .replace(/\s*days?$/, "d")
      .replace(/\s*months?$/, "mo")
      .replace(/\s*years?$/, "y") + " ago"
  );
}

function eventTimestamp(item: InboxItem, timeBasis: "captured" | "source"): string | null {
  return timeBasis === "source"
    ? item.item_timestamp ?? item.captured_at
    : item.captured_at;
}

function inboxPreview(item: InboxItem): string {
  const dslText = stringField(item.dsl_text);
  if (dslText) return dslText;
  if (item.structured_payload) return structuredSummary(item.structured_payload);
  return item.raw_text?.trim() || item.markdown?.trim() || "";
}

function structuredSummary(payload: Record<string, unknown>): string {
  const dsl =
    stringField(payload.signal_dsl) ??
    stringField(payload.trade_signal_dsl) ??
    stringField(payload.dsl);
  if (dsl) return dsl;

  const symbols = [
    ...arrayOfStrings(payload.symbols),
    stringField(payload.symbol),
    stringField(payload.ticker),
  ].filter(Boolean);
  const action = stringField(payload.action) ?? stringField(payload.side);
  const setup =
    stringField(payload.setup) ??
    stringField(payload.strategy) ??
    stringField(payload.name);
  const parts = [action, symbols.join(", "), setup].filter(Boolean);
  if (parts.length > 0) return parts.join(" · ");
  return JSON.stringify(payload);
}

function StructuredPreview({
  item,
  onEdit,
}: {
  item: InboxItem;
  onEdit: () => void;
}) {
  const lines = dslLines(item);
  if (lines.length > 0) {
    return (
      <span className="flex min-w-0 items-start gap-2">
        <span className="min-w-0 flex-1 space-y-1">
          {lines.slice(0, 3).map((line) => (
            <code
              key={line}
              className="block truncate font-mono text-[12px] text-fg"
              title={line}
            >
              {compactDsl(line)}
            </code>
          ))}
          {lines.length > 3 ? (
            <span className="text-xs text-muted-fg">
              +{lines.length - 3} more
            </span>
          ) : null}
        </span>
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={(event) => {
            event.stopPropagation();
            onEdit();
          }}
          className="h-7 shrink-0 gap-1 px-2 text-xs"
        >
          <Pencil className="size-3" aria-hidden />
          <span>Edit DSL</span>
        </Button>
      </span>
    );
  }
  return (
    <span className="flex min-w-0 items-center gap-2">
      <span className="min-w-0 flex-1 truncate">
        {structuredSummary(item.structured_payload ?? {})}
      </span>
      <Button
        type="button"
        size="sm"
        variant="outline"
        onClick={(event) => {
          event.stopPropagation();
          onEdit();
        }}
        className="h-7 shrink-0 gap-1 px-2 text-xs"
      >
        <Pencil className="size-3" aria-hidden />
        <span>Edit</span>
      </Button>
    </span>
  );
}

function SignalSummaryList({ payload }: { payload: Record<string, unknown> }) {
  const signals = arrayOfRecords(payload.signals);
  if (signals.length === 0) {
    return (
      <div className="rounded border border-border/50 bg-bg/50 p-3 text-xs text-muted-fg">
        {structuredSummary(payload)}
      </div>
    );
  }
  return (
    <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
      {signals.map((signal, index) => {
        const symbol = stringField(signal.symbol) ?? "—";
        const action = stringField(signal.action) ?? stringField(signal.item_kind) ?? "signal";
        const legs = arrayOfRecords(signal.option_legs);
        const entry = firstPrice(signal.entry);
        return (
          <div
            key={`${symbol}-${index}`}
            className="rounded border border-border/50 bg-bg/50 p-2 text-xs"
          >
            <div className="flex items-center justify-between gap-2">
              <span className="font-mono text-sm font-medium text-fg">
                {symbol}
              </span>
              <Badge variant="outline" className="text-[10px]">
                {action}
              </Badge>
            </div>
            <div className="mt-1 truncate text-muted-fg">
              {legs.length > 0
                ? legs.map(optionLegLabel).filter(Boolean).join(" · ")
                : stringField(signal.instrument_kind) ?? "equity"}
            </div>
            {entry ? (
              <div className="mt-1 font-mono text-[11px] text-emerald-300">
                entry {entry}
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

function DslEditor({
  item,
  compact = false,
  onDone,
}: {
  item: InboxItem;
  compact?: boolean;
  onDone?: () => void;
}) {
  const update = useUpdateInboxItem();
  const current = stringField(item.dsl_text) ?? dslFromPayload(item.structured_payload) ?? "";
  const [value, setValue] = useState(current);

  useEffect(() => {
    setValue(current);
  }, [current]);

  const dirty = value !== current;
  const save = () =>
    update.mutate(
      { id: item.id, dsl_text: value },
      { onSuccess: () => onDone?.() },
    );
  if (compact) {
    return (
      <div className="flex flex-col gap-2 rounded border border-border/60 bg-bg/50 p-2">
        <textarea
          value={value}
          onChange={(event) => setValue(event.currentTarget.value)}
          rows={Math.min(4, Math.max(2, value.split("\n").length))}
          className="w-full resize-y rounded border border-border/50 bg-black/20 p-2 font-mono text-xs leading-relaxed text-fg outline-none focus:ring-1 focus:ring-accent"
        />
        <div className="flex justify-end gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={onDone}
            disabled={update.isPending}
            className="h-7 px-2 text-xs"
          >
            Cancel
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={!dirty || update.isPending}
            onClick={save}
            className="h-7 px-2 text-xs"
          >
            Save DSL
          </Button>
        </div>
      </div>
    );
  }
  return (
    <section>
      <div className="mb-2 flex items-center justify-between gap-2">
        <h3 className="text-[10px] font-medium uppercase tracking-wider text-muted-fg/80">
          Signal DSL
        </h3>
        <Button
          size="sm"
          variant="outline"
          disabled={!dirty || update.isPending}
          onClick={save}
        >
          Save DSL
        </Button>
      </div>
      <textarea
        value={value}
        onChange={(event) => setValue(event.currentTarget.value)}
        rows={Math.min(6, Math.max(2, value.split("\n").length))}
        className="w-full resize-y rounded border border-border/50 bg-bg/50 p-3 font-mono text-xs leading-relaxed text-fg outline-none focus:ring-1 focus:ring-accent"
      />
    </section>
  );
}

function StructuredBlock({ payload }: { payload: Record<string, unknown> }) {
  return (
    <dl className="grid gap-2 rounded border border-border/50 bg-bg/50 p-3 text-xs sm:grid-cols-[max-content_1fr]">
      {Object.entries(payload).map(([key, value]) => (
        <div key={key} className="contents">
          <dt className="font-medium text-muted-fg">{labelize(key)}</dt>
          <dd className="min-w-0 break-words text-fg">
            {formatStructuredValue(value)}
          </dd>
        </div>
      ))}
    </dl>
  );
}

function formatStructuredValue(value: unknown): React.ReactNode {
  if (value === null || value === undefined || value === "") {
    return <span className="text-muted-fg/70">—</span>;
  }
  if (Array.isArray(value)) {
    if (value.length === 0) return "[]";
    if (value.every((item) => typeof item !== "object" || item === null)) {
      return value.map(String).join(", ");
    }
    return (
      <pre className="overflow-auto whitespace-pre-wrap rounded bg-black/20 p-2 font-mono text-[11px] text-muted-fg">
        {JSON.stringify(value, null, 2)}
      </pre>
    );
  }
  if (typeof value === "object") {
    return (
      <pre className="overflow-auto whitespace-pre-wrap rounded bg-black/20 p-2 font-mono text-[11px] text-muted-fg">
        {JSON.stringify(value, null, 2)}
      </pre>
    );
  }
  return String(value);
}

function stringField(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

function dslFromPayload(payload: Record<string, unknown> | null): string | null {
  if (!payload) return null;
  const topLevel =
    stringField(payload.signal_dsl) ??
    stringField(payload.trade_signal_dsl) ??
    stringField(payload.dsl);
  if (topLevel) return topLevel;

  const signalLines = arrayOfRecords(payload.signals)
    .map((signal) => stringField(signal.signal_dsl))
    .filter((line): line is string => line !== null);
  return signalLines.length > 0 ? signalLines.join("\n") : null;
}

function dslLines(item: InboxItem): string[] {
  const dsl = stringField(item.dsl_text) ?? dslFromPayload(item.structured_payload);
  if (!dsl) return [];
  return dsl
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);
}

function compactDsl(line: string): string {
  return line.replace(/^SIGNAL\s+/i, "");
}

function arrayOfRecords(value: unknown): Array<Record<string, unknown>> {
  if (!Array.isArray(value)) return [];
  return value.filter(
    (item): item is Record<string, unknown> =>
      typeof item === "object" && item !== null && !Array.isArray(item),
  );
}

function firstPrice(value: unknown): string | null {
  const levels = arrayOfRecords(value);
  const price = levels
    .map((level) => level.price)
    .find((candidate) => typeof candidate === "number" || typeof candidate === "string");
  return price == null ? null : String(price);
}

function optionLegLabel(leg: Record<string, unknown>): string {
  const underlying = stringField(leg.underlying);
  const expiration = stringField(leg.expiration);
  const strike = leg.strike == null ? null : String(leg.strike);
  const right = stringField(leg.right);
  const suffix = right === "call" ? "C" : right === "put" ? "P" : "";
  return [underlying, expiration, strike ? `${strike}${suffix}` : null]
    .filter(Boolean)
    .join(" ");
}

function arrayOfStrings(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((item) => (typeof item === "string" ? item.trim() : ""))
    .filter(Boolean);
}

function labelize(value: string): string {
  return value.replace(/_/g, " ");
}
